import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { JobStatus } from '@fin/shared/enums';
import { SERVICE_LABELS, STATUS_LABELS } from '@fin/shared/enums';
import { BRAND } from '@fin/shared/brand';
import { buildWhatsAppLink, type WhatsAppTemplateId } from '@fin/shared/whatsapp';
import { useCustomer, useJob, useUpdateStatus, useUploadProof, useAcknowledgeOverdue, useUpdateWorkerNotes } from '../lib/queries';
import { useI18n } from '../lib/i18n';
import { useAuth } from '../lib/auth';

export function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const lang = locale === 'ar' ? 'ar-AE' : 'en-AE';
  const job = useJob(id);
  const customer = useCustomer(job.data?.customerId);
  const updateStatus = useUpdateStatus(id);
  const uploadProof = useUploadProof(id);
  const ackOverdue = useAcknowledgeOverdue(id);
  const [reason, setReason] = useState('');
  const [showHold, setShowHold] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);

  if (job.isPending) {
    return <p className="p-4 text-sm text-stone-500">{t('job.loading')}</p>;
  }
  if (job.isError) {
    return (
      <p className="p-4 text-sm" style={{ color: BRAND.danger }}>
        {t('job.failed')} {(job.error as Error).message}
      </p>
    );
  }
  if (!job.data) return null;

  const j = job.data;
  // Whether the logged-in worker is assigned to this job. Workers can open any
  // job, but only their own expose the actionable workflow. Default true so a
  // pre-`mine` backend response still behaves (workers only fetched their own).
  const mine = j.mine ?? true;
  const when = new Date(j.scheduledAt);

  async function setStatus(status: JobStatus, withReason?: string) {
    try {
      await updateStatus.mutateAsync({ status, reason: withReason });
      setShowHold(false);
      setReason('');
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <main className="min-h-screen pb-24" style={{ backgroundColor: BRAND.surfaceMuted }}>
      <header
        className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3 text-white shadow-lg"
        style={{
          backgroundImage: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primarySoft} 100%)`,
        }}
      >
        <Link
          to="/"
          className="flex h-10 items-center gap-1.5 rounded-full bg-white/15 px-3 text-white transition hover:bg-white/25 active:scale-95"
          aria-label={t('job.back')}
        >
          <span aria-hidden className="text-lg leading-none">←</span>
          <span className="text-xs font-medium">{t('job.back')}</span>
        </Link>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider opacity-80">{t('job.label')}</p>
          <p className="truncate text-base font-semibold sm:text-lg">
            {SERVICE_LABELS[j.serviceType as keyof typeof SERVICE_LABELS]?.[locale] ?? j.serviceType}
          </p>
        </div>
      </header>

      <section className="space-y-4 p-4">
        <Card>
          <p className="text-xs uppercase tracking-wide text-stone-500">{t('job.when')}</p>
          <p className="mt-1 font-semibold" style={{ color: BRAND.primary }}>
            {j.anytime
              ? `${when.toLocaleDateString(lang, { timeZone: 'Asia/Dubai', dateStyle: 'full' })} · ${t('job.anytime')}`
              : when.toLocaleString(lang, { timeZone: 'Asia/Dubai', dateStyle: 'full', timeStyle: 'short' })}
          </p>
          <p className="mt-1 text-xs text-stone-500">
            {t('job.estimated')} {j.estimatedDurationMinutes} {t('job.minutes')} · {j.priority} {t('job.priority')} · {t('job.status')} {STATUS_LABELS[j.status]?.[locale] ?? j.status}
          </p>
        </Card>

        <Card>
          <p className="text-xs uppercase tracking-wide text-stone-500">{t('job.customer')}</p>
          {customer.isPending && <p className="text-sm text-stone-500">{t('job.loading_customer')}</p>}
          {customer.data && (
            <>
              <p className="mt-1 font-semibold" style={{ color: BRAND.primary }}>
                {customer.data.name}
              </p>
              <p className="mt-2 text-sm text-stone-700">
                {customer.data.address.building && `${customer.data.address.building}, `}
                {customer.data.address.area}, {customer.data.address.emirate.replace('_', ' ')}
              </p>
              {customer.data.address.makaniNumber && (
                <p className="text-xs text-stone-500">{t('job.makani')} {customer.data.address.makaniNumber}</p>
              )}
            </>
          )}
        </Card>

        {customer.data && <LocationCard address={customer.data.address} customerName={customer.data.name} />}

        {mine && (
          <OverdueBanner
            job={j}
            pending={ackOverdue.isPending}
            onAck={async (etaMinutes) => {
              try {
                await ackOverdue.mutateAsync({ etaMinutes });
              } catch (err) {
                alert(err instanceof Error ? err.message : String(err));
              }
            }}
          />
        )}

        {(() => {
          const ids = j.assignedWorkerIds && j.assignedWorkerIds.length > 0
            ? j.assignedWorkerIds
            : j.assignedWorkerId
              ? [j.assignedWorkerId]
              : [];
          if (ids.length <= 1) return null;
          const nameById = new Map((j.assignedWorkerNames ?? []).map((w) => [w.id, w.name]));
          return (
            <Card>
              <p className="text-xs uppercase tracking-wide text-stone-500">{t('job.team')}</p>
              <ul className="mt-1 space-y-1 text-sm text-stone-700">
                {ids.map((id, i) => {
                  const name = nameById.get(id) || `Worker ${id.slice(-4)}`;
                  return (
                    <li key={id} className="flex items-center gap-2">
                      <span
                        aria-hidden
                        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-stone-200 text-[11px] font-semibold text-stone-700"
                      >
                        {name.trim().charAt(0).toUpperCase() || '?'}
                      </span>
                      <span>{name}</span>
                      {i === 0 && (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                          ★ Primary
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Card>
          );
        })()}

        {j.description && (
          <Card>
            <p className="text-xs uppercase tracking-wide text-stone-500">{t('job.notes')}</p>
            <p className="mt-1 whitespace-pre-line text-sm text-stone-700">{j.description}</p>
          </Card>
        )}

        {/* Workers can open any job for full team visibility, but only act on
            their own. For others' jobs show a view-only notice and read-only
            notes; hide the workflow, notes editor, and proof upload. */}
        {mine ? (
          <WorkerNotes jobId={j.id} initial={j.workerNotes ?? ''} />
        ) : (
          j.workerNotes && (
            <Card>
              <p className="text-xs uppercase tracking-wide text-stone-500">{t('job.worker_notes')}</p>
              <p className="mt-1 whitespace-pre-line text-sm text-stone-700">{j.workerNotes}</p>
            </Card>
          )
        )}

        {!mine && (
          <Card>
            <p className="text-sm text-stone-600">👁 {t('job.not_assigned')}</p>
          </Card>
        )}

        {mine && customer.data && j.status !== 'on_hold' && (
          <ForcedWorkflow
            job={j}
            customer={customer.data}
            scheduledTime={when.toLocaleTimeString(lang, { timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit' })}
            serviceType={SERVICE_LABELS[j.serviceType as keyof typeof SERVICE_LABELS]?.en ?? j.serviceType}
            pending={updateStatus.isPending}
            onAction={setStatus}
            onHold={() => setShowHold(true)}
            onComplete={() => setShowCompleteConfirm(true)}
          />
        )}

        {j.status === 'on_hold' && (
          <Card>
            <p
              className="text-sm font-semibold"
              style={{ color: '#A16207' }}
            >
              ⏸ {t('workflow.held.title')}
            </p>
            <p className="mt-1 text-xs text-stone-600">{t('workflow.held.body')}</p>
          </Card>
        )}

        {mine && (j.status === 'in_progress' || j.status === 'completed') && (
          <ProofSection
            job={j}
            uploading={uploadProof.isPending}
            onUpload={(file, kind) => uploadProof.mutate({ file, kind })}
          />
        )}
      </section>

      {/* Bottom hold-reason sheet (slides over when worker presses Hold) */}
      {showHold && (
        <div
          className="fixed inset-x-0 bottom-0 z-10 border-t bg-white p-3"
          style={{ borderColor: BRAND.border }}
        >
          <div className="flex flex-col gap-2">
            <textarea
              autoFocus
              rows={3}
              className="rounded border border-stone-300 px-3 py-2 text-sm"
              placeholder={t('job.hold.placeholder')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <p className="text-[11px] text-stone-500">{t('workflow.hold.min')}</p>
            <div className="flex gap-2">
              <button
                className="flex-1 rounded border border-stone-300 py-2 text-sm"
                onClick={() => {
                  setShowHold(false);
                  setReason('');
                }}
              >
                {t('job.hold.cancel')}
              </button>
              <button
                className="flex-1 rounded py-2 text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: '#A16207' }}
                disabled={reason.trim().length < 10 || updateStatus.isPending}
                onClick={() => void setStatus('on_hold', reason.trim())}
              >
                {t('job.hold.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCompleteConfirm && (
        <CompleteConfirmModal
          pending={updateStatus.isPending}
          onCancel={() => setShowCompleteConfirm(false)}
          onConfirm={async () => {
            try {
              await updateStatus.mutateAsync({ status: 'completed' });
              setShowCompleteConfirm(false);
              navigate('/', { replace: true });
            } catch (err) {
              alert(err instanceof Error ? err.message : String(err));
            }
          }}
        />
      )}
    </main>
  );
}

/**
 * Banner that appears on overdue jobs (scheduled time has passed but worker
 * hasn't started). Lets the worker tap an ETA to acknowledge — silences the
 * recurring overdue push and tells dispatch they're aware. Once acked it
 * shows the recorded ETA and time of acknowledgement.
 */
function OverdueBanner({
  job,
  onAck,
  pending,
}: {
  job: { scheduledAt: string; status: JobStatus; overdueAck?: { at: string; etaMinutes: number } };
  onAck: (etaMinutes: number) => Promise<void> | void;
  pending: boolean;
}) {
  const { t, locale } = useI18n();
  const lang = locale === 'ar' ? 'ar-AE' : 'en-AE';
  const sched = new Date(job.scheduledAt);
  const lateMin = Math.floor((Date.now() - sched.getTime()) / 60_000);
  const isOverdue =
    lateMin > 0 && !['in_progress', 'completed', 'cancelled', 'on_hold'].includes(job.status);
  if (!isOverdue) return null;

  if (job.overdueAck) {
    const ackedAt = new Date(job.overdueAck.at).toLocaleTimeString(lang, {
      timeZone: 'Asia/Dubai',
      hour: '2-digit',
      minute: '2-digit',
    });
    return (
      <div
        className="rounded-xl border-l-4 bg-emerald-50 px-4 py-3 text-sm"
        style={{ borderLeftColor: '#10B981' }}
      >
        <p className="font-semibold text-emerald-800">
          {t('job.overdue.acked')} · ETA {job.overdueAck.etaMinutes} {t('job.overdue.minutes_short')}
        </p>
        <p className="mt-0.5 text-xs text-emerald-700">
          {t('job.overdue.acked_at')} {ackedAt}
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border-l-4 bg-orange-50 px-4 py-3"
      style={{ borderLeftColor: '#F97316' }}
    >
      <p className="text-sm font-semibold text-orange-900">
        ⏰ {t('job.overdue.title')} {lateMin} {t('job.overdue.minutes_short')}
      </p>
      <p className="mt-1 text-xs text-orange-800">{t('job.overdue.prompt')}</p>
      <div className="mt-2 grid grid-cols-4 gap-2">
        {[5, 10, 15, 30].map((m) => (
          <button
            key={m}
            type="button"
            disabled={pending}
            onClick={() => void onAck(m)}
            className="rounded-md bg-orange-600 px-2 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:opacity-50"
          >
            {m} {t('job.overdue.minutes_short')}
          </button>
        ))}
      </div>
    </div>
  );
}

function ActionButtons(_p: {
  status: JobStatus;
  scheduledAt: string;
  onAction: (s: JobStatus) => void;
  onHold: () => void;
  onComplete: () => void;
  pending: boolean;
}) {
  // Replaced by ForcedWorkflow. Kept as a stub so existing imports compile.
  return null;
}

function ProofSection({
  job,
  uploading,
  onUpload,
}: {
  job: { proof?: { id: string; key: string; kind: string; uploadedAt: string }[] };
  uploading: boolean;
  onUpload: (file: File, kind: 'before' | 'after' | 'parts' | 'video') => void;
}) {
  const beforeRef = useRef<HTMLInputElement>(null);
  const afterRef = useRef<HTMLInputElement>(null);
  const partsRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const [pendingKind, setPendingKind] = useState<'before' | 'after' | 'parts' | 'video' | null>(null);
  const { t } = useI18n();
  const photos = job.proof ?? [];

  function makePhotoHandler(k: 'before' | 'after' | 'parts') {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (f) {
        setPendingKind(k);
        onUpload(f, k);
      }
      e.target.value = '';
    };
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-stone-500">{t('proof.title')}</p>
        <span className="text-xs text-stone-500">{photos.length} {photos.length === 1 ? t('proof.photos') : t('proof.photos_plural')}</span>
      </div>
      <input ref={beforeRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" capture="environment" className="hidden" onChange={makePhotoHandler('before')} />
      <input ref={afterRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" capture="environment" className="hidden" onChange={makePhotoHandler('after')} />
      <input ref={partsRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" capture="environment" className="hidden" onChange={makePhotoHandler('parts')} />
      <input
        ref={videoRef}
        type="file"
        accept="video/mp4,video/quicktime,video/webm"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) {
            if (f.size > 75 * 1024 * 1024) {
              alert(t('proof.video_too_big'));
            } else {
              setPendingKind('video');
              onUpload(f, 'video');
            }
          }
          e.target.value = '';
        }}
      />

      {/* Three independent camera buttons — worker can tap any in any order. */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        {([
          { k: 'before' as const, ref: beforeRef },
          { k: 'after' as const, ref: afterRef },
          { k: 'parts' as const, ref: partsRef },
        ]).map(({ k, ref }) => {
          const count = photos.filter((p) => p.kind === k).length;
          const has = count > 0;
          const isUploading = uploading && pendingKind === k;
          return (
            <button
              key={k}
              className="flex flex-col items-center gap-1 rounded-lg border-2 border-dashed py-3 text-xs font-medium disabled:opacity-50"
              style={{
                borderColor: has ? BRAND.success : BRAND.border,
                backgroundColor: has ? '#F0FDF4' : 'white',
                color: has ? BRAND.success : BRAND.primary,
              }}
              disabled={uploading}
              onClick={() => ref.current?.click()}
            >
              <span className="text-xl leading-none">{has ? '✓' : '📷'}</span>
              <span>{isUploading ? t('proof.uploading') : `${t('proof.add')} ${t(`proof.${k}`)}`}</span>
              {has && <span className="text-[10px] opacity-80">{count}</span>}
            </button>
          );
        })}
      </div>
      <button
        className="mt-2 w-full rounded border-2 border-dashed py-2.5 text-sm font-medium disabled:opacity-50"
        style={{ borderColor: BRAND.border, color: BRAND.primary }}
        disabled={uploading}
        onClick={() => videoRef.current?.click()}
      >
        {uploading && pendingKind === 'video' ? t('proof.uploading') : `🎥 ${t('proof.add_video')}`}
      </button>
      {photos.length > 0 && (
        <ul className="mt-3 space-y-2">
          {photos.map((p) => (
            <li key={p.id} className="flex items-center justify-between rounded bg-stone-50 px-2 py-1.5 text-xs">
              <span className="text-stone-700">
                <span
                  className="mr-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium uppercase"
                  style={{
                    backgroundColor:
                      p.kind === 'video' || p.kind === 'intake_video'
                        ? '#1E3A8A'
                        : p.kind.startsWith('intake')
                          ? '#A16207'
                          : '#E7E5E4',
                    color:
                      p.kind === 'video' || p.kind === 'intake_video' || p.kind.startsWith('intake')
                        ? 'white'
                        : BRAND.text,
                  }}
                >
                  {p.kind.replace('intake_', 'customer ')}
                </span>
                {new Date(p.uploadedAt).toLocaleTimeString('en-AE', { timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className="font-mono text-[10px] text-stone-400">{p.key.split('/').pop()}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * Worker-writable field notes. Workers can jot what they found / did on site;
 * saved via PATCH /jobs/:id (offline-queueable). Separate from the admin's
 * internal notes and the customer-facing description.
 */
function WorkerNotes({ jobId, initial }: { jobId: string; initial: string }) {
  const { t } = useI18n();
  const save = useUpdateWorkerNotes(jobId);
  const [value, setValue] = useState(initial);
  const [saved, setSaved] = useState(false);

  // Keep in sync if the job refetches with newer notes and the field is clean.
  useEffect(() => {
    setValue((cur) => (cur === '' || cur === initial ? initial : cur));
  }, [initial]);

  const dirty = value.trim() !== (initial ?? '').trim();

  async function onSave() {
    try {
      await save.mutateAsync(value.trim());
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Card>
      <p className="text-xs uppercase tracking-wide text-stone-500">{t('job.worker_notes')}</p>
      <textarea
        rows={3}
        className="mt-2 w-full rounded border border-stone-300 px-3 py-2 text-sm"
        placeholder={t('job.worker_notes_placeholder')}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={4000}
      />
      <button
        type="button"
        className="mt-2 w-full rounded-lg py-2.5 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98] disabled:opacity-50"
        style={{ backgroundColor: BRAND.primary }}
        disabled={!dirty || save.isPending}
        onClick={() => void onSave()}
      >
        {save.isPending ? t('proof.uploading') : saved ? `✓ ${t('job.worker_notes_saved')}` : t('job.worker_notes_save')}
      </button>
    </Card>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-white p-4 shadow-sm" style={{ borderColor: BRAND.border }}>
      {children}
    </div>
  );
}

interface AddressLike {
  building?: string;
  unit?: string;
  street?: string;
  area: string;
  emirate: string;
  makaniNumber?: string;
  geo?: { lat: number; lng: number };
}

function formatAddressLine(a: AddressLike): string {
  return [a.building, a.street, a.area, a.emirate.replace('_', ' '), 'United Arab Emirates']
    .filter(Boolean)
    .join(', ');
}

/**
 * Location card — shows an embedded Google Map of the customer's address
 * (no API key required) plus tap-friendly Navigate / Open in Maps / Copy
 * actions. Prefers geo coords when available, falls back to the address
 * string. The Navigate button uses the Google Maps directions deep link
 * which on mobile opens the native Maps app.
 */
function LocationCard({ address, customerName }: { address: AddressLike; customerName: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const addressLine = formatAddressLine(address);
  const query = address.geo ? `${address.geo.lat},${address.geo.lng}` : addressLine;
  // Google's `maps?q=…&output=embed` iframe is now frame-blocked
  // (net::ERR_BLOCKED_BY_RESPONSE). Use a keyless OpenStreetMap embed when we
  // have coordinates; otherwise skip the embed and show a tap-to-open card
  // (free-text addresses can't be embedded without geocoding).
  const osmEmbed = address.geo
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${address.geo.lng - 0.008}%2C${address.geo.lat - 0.006}%2C${address.geo.lng + 0.008}%2C${address.geo.lat + 0.006}&layer=mapnik&marker=${address.geo.lat}%2C${address.geo.lng}`
    : null;
  const navigateUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}&travelmode=driving`;
  const viewUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(addressLine);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignored — clipboard not available
    }
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-stone-500">{t('job.location')}</p>
          <p className="mt-0.5 text-sm font-medium text-stone-700">{addressLine}</p>
          {address.makaniNumber && (
            <p className="text-xs text-stone-500">
              {t('job.makani')} <span className="font-mono">{address.makaniNumber}</span>
            </p>
          )}
        </div>
      </div>

      {osmEmbed ? (
        <div className="mt-3 overflow-hidden rounded-lg border" style={{ borderColor: BRAND.border }}>
          <iframe
            title={`Map of ${customerName}`}
            src={osmEmbed}
            className="block h-48 w-full border-0 sm:h-56"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      ) : (
        <a
          href={viewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 flex h-24 w-full items-center justify-center gap-2 rounded-lg border text-sm font-medium sm:h-28"
          style={{ borderColor: BRAND.border, backgroundColor: BRAND.surfaceMuted, color: BRAND.primary }}
        >
          <span aria-hidden className="text-xl">🗺️</span> {t('job.open_maps')} ↗
        </a>
      )}

      <div className="mt-3 grid grid-cols-3 gap-2">
        <a
          href={navigateUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="col-span-2 flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98]"
          style={{ backgroundColor: BRAND.primary }}
        >
          <span aria-hidden>🧭</span> {t('job.navigate')}
        </a>
        <button
          type="button"
          onClick={copy}
          className="rounded-lg border py-2.5 text-xs font-medium"
          style={{ borderColor: BRAND.border, color: BRAND.primary }}
        >
          {copied ? `✓ ${t('job.address_copied')}` : t('job.copy_address')}
        </button>
      </div>
      <a
        href={viewUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 block text-center text-[11px] hover:underline"
        style={{ color: BRAND.accent }}
      >
        {t('job.open_maps')} ↗
      </a>
    </Card>
  );
}

/**
 * Legacy free-form WhatsApp row. Replaced by ForcedWorkflow which gates
 * the on_my_way template behind the workflow stepper. Kept as a stub.
 */
function WhatsAppRow(_p: {
  phone: string;
  customerName: string;
  serviceType: string;
  scheduledTime: string;
  jobId: string;
  status: JobStatus;
}) {
  return null;
}

/**
 * Branded confirmation modal for "End job" / mark complete. Replaces the
 * native window.confirm() with a styled bottom-sheet on mobile / centered
 * dialog on desktop. ESC + backdrop tap cancel.
 */
function CompleteConfirmModal({
  pending,
  onCancel,
  onConfirm,
}: {
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useI18n();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, pending]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center"
      onClick={() => !pending && onCancel()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-3">
          <span
            className="flex h-11 w-11 items-center justify-center rounded-full text-2xl"
            style={{ backgroundColor: '#F0FDF4', color: BRAND.success }}
          >
            ✓
          </span>
          <div>
            <h2 className="text-base font-semibold" style={{ color: BRAND.text }}>
              {t('job.complete_title')}
            </h2>
            <p className="text-xs text-stone-500">{t('job.complete_subtitle')}</p>
          </div>
        </div>
        <p className="mb-4 text-sm text-stone-700">{t('job.complete_confirm')}</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="flex-1 rounded-lg border py-2.5 text-sm font-medium text-stone-700 disabled:opacity-50"
            style={{ borderColor: BRAND.border }}
          >
            {t('wa.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="flex-[2] rounded-lg py-2.5 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98] disabled:opacity-50"
            style={{ backgroundColor: BRAND.success }}
          >
            {pending ? t('proof.uploading') : t('workflow.cta.end')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Mobile-friendly bottom-sheet modal letting the worker pick an ETA in
 * minutes via tap-friendly chips (or custom). Shows live arrival clock.
 */
function EtaPickerModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: (mins: number) => void;
}) {
  const { t } = useI18n();
  const PRESETS = [5, 10, 15, 20, 30, 45, 60];
  const [mins, setMins] = useState(15);
  const [custom, setCustom] = useState('');

  const finalMins = (() => {
    const n = Number(custom);
    if (custom.trim() && Number.isFinite(n) && n > 0) return Math.min(240, Math.round(n));
    return mins;
  })();
  const arrival = new Date(Date.now() + finalMins * 60_000);
  const clock = arrival.toLocaleTimeString('en-AE', {
    timeZone: 'Asia/Dubai',
    hour: '2-digit',
    minute: '2-digit',
  });

  // Close on ESC.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center gap-2">
          <span
            className="flex h-9 w-9 items-center justify-center rounded-full text-lg"
            style={{ backgroundColor: '#E6F8EE', color: '#075E54' }}
          >
            📍
          </span>
          <h2 className="text-base font-semibold" style={{ color: BRAND.primary }}>
            {t('wa.eta_title')}
          </h2>
        </div>
        <p className="mb-4 text-xs text-stone-500">{t('wa.eta_subtitle')}</p>

        <div className="grid grid-cols-4 gap-2">
          {PRESETS.map((p) => {
            const active = !custom.trim() && mins === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => {
                  setMins(p);
                  setCustom('');
                }}
                className="rounded-lg border py-3 text-sm font-semibold transition active:scale-95"
                style={{
                  borderColor: active ? BRAND.primary : BRAND.border,
                  backgroundColor: active ? BRAND.primary : '#fff',
                  color: active ? '#fff' : BRAND.primary,
                }}
              >
                {p} <span className="text-[10px] font-normal opacity-80">min</span>
              </button>
            );
          })}
        </div>

        <label className="mt-3 flex items-center gap-2 text-xs text-stone-600">
          <span className="whitespace-nowrap">{t('wa.eta_custom')}:</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={240}
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="—"
            className="w-full rounded border border-stone-300 px-2 py-1.5 text-sm"
          />
        </label>

        <div
          className="mt-4 rounded-lg border px-3 py-2 text-xs"
          style={{ borderColor: BRAND.border, backgroundColor: BRAND.surfaceMuted }}
        >
          <span className="text-stone-500">{t('wa.eta_arrival')} </span>
          <span className="font-semibold" style={{ color: BRAND.primary }}>
            {clock}
          </span>
          <span className="text-stone-500"> · {finalMins} min</span>
        </div>

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-lg border py-2.5 text-sm font-medium text-stone-600"
            style={{ borderColor: BRAND.border }}
          >
            {t('wa.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(finalMins)}
            className="flex-[2] rounded-lg py-2.5 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98]"
            style={{ backgroundColor: '#25D366' }}
          >
            {t('wa.eta_send')}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Forced workflow card. Renders a 4-step stepper and exactly ONE call-to-action
 * for the worker's current step. Locked steps render disabled with a tooltip
 * "Complete previous step first." Workers cannot:
 *   - skip steps,
 *   - go backwards,
 *   - cancel,
 *   - end the job before uploading 1+ before, 1+ after, AND 1+ parts photo,
 *   - resume from on_hold (admin-only path).
 *
 * Step 1 (`On my way`) bundles the WhatsApp `customer_on_the_way` template and
 * the status update into a single tap — picking ETA opens WA + transitions
 * status to `on_my_way`.
 */
function ForcedWorkflow({
  job,
  customer,
  scheduledTime,
  serviceType,
  pending,
  onAction,
  onHold,
  onComplete,
}: {
  job: { id: string; status: JobStatus; scheduledAt: string; proof?: { kind: string }[]; assignedWorkerNames?: { id: string; name: string }[]; assignedWorkerId?: string };
  customer: {
    name: string;
    phone: string;
    whatsappPhone?: string;
    address?: { building?: string; street?: string; area: string; emirate: string };
  };
  scheduledTime: string;
  serviceType: string;
  pending: boolean;
  onAction: (s: JobStatus, reason?: string) => void;
  onHold: () => void;
  onComplete: () => void;
}) {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const [etaOpen, setEtaOpen] = useState(false);

  // Worker display name: prefer the matching entry in assignedWorkerNames,
  // fall back to the email local-part so admin sees something human-readable.
  const workerName = (() => {
    const names = job.assignedWorkerNames ?? [];
    const mine = names.find((n) => n.id === job.assignedWorkerId) ?? names[0];
    if (mine?.name) return mine.name;
    const email = user?.email ?? '';
    return email ? email.split('@')[0] : 'Worker';
  })();

  const customerAddress = customer.address
    ? [customer.address.building, customer.address.street, customer.address.area, customer.address.emirate?.replace('_', ' ')]
        .filter(Boolean)
        .join(', ')
    : '';

  if (job.status === 'completed') {
    return (
      <Card>
        <p className="text-center text-sm font-semibold" style={{ color: BRAND.success }}>
          ✓ {t('job.action.completed')}
        </p>
      </Card>
    );
  }
  if (job.status === 'cancelled') {
    return (
      <Card>
        <p className="text-center text-sm text-stone-500">{t('job.action.cancelled')}</p>
      </Card>
    );
  }

  // Future-day lock — same behaviour as before.
  const dubaiDate = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' });
  const todayDubai = dubaiDate(new Date().toISOString());
  const jobDay = dubaiDate(job.scheduledAt);
  const isFuture =
    jobDay > todayDubai && ['scheduled', 'assigned', 'accepted'].includes(job.status);
  if (isFuture) {
    const lang = locale === 'ar' ? 'ar-AE' : 'en-AE';
    const niceDay = new Date(job.scheduledAt).toLocaleDateString(lang, {
      timeZone: 'Asia/Dubai',
      weekday: 'long',
      day: 'numeric',
      month: 'short',
    });
    return (
      <Card>
        <p className="text-center text-sm font-semibold" style={{ color: BRAND.primary }}>
          {t('job.action.future_lock')} {niceDay}
        </p>
      </Card>
    );
  }

  // Map status → current step index (0-based).
  const stepIdx =
    job.status === 'on_my_way'
      ? 1
      : job.status === 'in_progress'
        ? 2
        : 0;
  const proof = job.proof ?? [];
  const hasBefore = proof.some((p) => p.kind === 'before');
  const hasAfter = proof.some((p) => p.kind === 'after');
  const hasParts = proof.some((p) => p.kind === 'parts');
  // Proof photos are encouraged but NOT required to end the job. The worker
  // can upload before/after/parts at any time during the in_progress step,
  // and the End-job CTA is always available alongside them.
  const visibleStep = stepIdx;

  const STEPS = [
    { i: 0, label: t('workflow.step1') },
    { i: 1, label: t('workflow.step2') },
    { i: 2, label: t('workflow.step4') }, // "End job" — proof is optional, merged into this step
  ];

  function sendWithEta(mins: number) {
    const arrival = new Date(Date.now() + mins * 60_000);
    const clock = arrival.toLocaleTimeString('en-AE', {
      timeZone: 'Asia/Dubai',
      hour: '2-digit',
      minute: '2-digit',
    });
    const eta = `~${mins} min (around ${clock})`;
    const adminPhone = (import.meta.env.VITE_ADMIN_WHATSAPP as string | undefined) ?? '+971528945495';
    const url = buildWhatsAppLink(adminPhone, 'admin_worker_on_the_way', {
      workerName,
      customerName: customer.name,
      customerAddress,
      serviceType,
      scheduledTime,
      eta,
      jobId: job.id.slice(-8),
    });
    setEtaOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
    // Status update fires regardless of whether the WA window opens — admin
    // is told and dispatch sees the worker is moving.
    onAction('on_my_way');
  }

  return (
    <Card>
      <p className="text-xs uppercase tracking-wide text-stone-500">{t('workflow.title')}</p>
      <ol className="mt-3 grid grid-cols-3 gap-1.5">
        {STEPS.map((s) => {
          const done = s.i < visibleStep;
          const current = s.i === visibleStep;
          return (
            <li key={s.i} className="flex flex-col items-center text-center">
              <span
                className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold"
                style={{
                  backgroundColor: done ? BRAND.success : current ? BRAND.primary : '#E7E5E4',
                  color: done || current ? '#fff' : '#78716C',
                }}
              >
                {done ? '✓' : s.i + 1}
              </span>
              <span
                className="mt-1 text-[10px] leading-tight"
                style={{ color: current ? BRAND.primary : done ? BRAND.success : '#78716C' }}
              >
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>

      {/* Step 1 — Tell customer "On my way" (forced WhatsApp template). */}
      {visibleStep === 0 && (
        <div className="mt-4">
          <button
            className="w-full rounded-lg py-3 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98] disabled:opacity-50"
            style={{ backgroundColor: '#25D366' }}
            disabled={pending}
            onClick={() => setEtaOpen(true)}
          >
            {t('workflow.cta.on_my_way')}
          </button>
          <p className="mt-2 text-[11px] text-stone-500">{t('wa.hint')}</p>
        </div>
      )}

      {/* Step 2 — Start job. */}
      {visibleStep === 1 && (
        <div className="mt-4">
          <button
            className="w-full rounded-lg py-3 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98] disabled:opacity-50"
            style={{ backgroundColor: BRAND.primary }}
            disabled={pending}
            onClick={() => onAction('in_progress')}
          >
            {t('workflow.cta.start')}
          </button>
        </div>
      )}

      {/* Step 3 — Proof of work (optional) + End job. Workers may upload
          before/after/parts photos using the camera buttons in the Proof of
          Work card below; this step always exposes the End-job CTA so they
          are not blocked from finishing if photos aren't applicable. */}
      {visibleStep === 2 && (
        <div className="mt-4 space-y-2">
          <div className="rounded-lg border-l-4 px-3 py-2 text-xs" style={{ borderLeftColor: BRAND.primary, backgroundColor: BRAND.surfaceMuted }}>
            <p className="font-semibold" style={{ color: BRAND.primary }}>
              📷 {t('workflow.step3')}
            </p>
            <p className="mt-0.5 text-stone-600">{t('workflow.proof_optional')}</p>
            <ul className="mt-1 space-y-0.5">
              {[
                { ok: hasBefore, label: t('proof.before') },
                { ok: hasAfter, label: t('proof.after') },
                { ok: hasParts, label: t('proof.parts') },
              ].map((r) => (
                <li key={r.label} className="text-[11px]" style={{ color: r.ok ? BRAND.success : '#78716C' }}>
                  {r.ok ? '✓' : '○'} {r.label}
                </li>
              ))}
            </ul>
          </div>
          <button
            className="w-full rounded-lg py-3 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98] disabled:opacity-50"
            style={{ backgroundColor: BRAND.success }}
            disabled={pending}
            onClick={onComplete}
          >
            {t('workflow.cta.end')}
          </button>
          <button
            className="w-full rounded-lg border py-2.5 text-sm font-medium"
            style={{ borderColor: '#A16207', color: '#A16207' }}
            disabled={pending}
            onClick={onHold}
          >
            {t('workflow.cta.hold')}
          </button>
        </div>
      )}

      {etaOpen && <EtaPickerModal onCancel={() => setEtaOpen(false)} onConfirm={sendWithEta} />}
    </Card>
  );
}
